import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Crown, ArrowSquareOut, Monitor } from "@phosphor-icons/react";
import { ReferralCard } from "../components/ReferralCard";
import { CouponBox, applyCoupon, couponLabel } from "../components/CouponBox";

function UsageBar({ label, used, limit }) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div data-testid={`usage-${label}`}>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono-nums font-semibold">{used}{limit ? ` / ${limit}` : " · ilimitado"}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-indigo-500"}`} style={{ width: `${limit ? pct : 100}%`, opacity: limit ? 1 : 0.25 }} />
      </div>
    </div>
  );
}

const STATUS_LABEL = { trial: "Teste grátis", active: "Ativo", past_due: "Pagamento pendente", expired: "Expirado", canceled: "Cancelado", owner: "Dono do sistema" };
const STATUS_CLASS = { trial: "bg-amber-50 text-amber-700 border-amber-200", active: "bg-emerald-50 text-emerald-700 border-emerald-200", past_due: "bg-rose-50 text-rose-700 border-rose-200", expired: "bg-rose-50 text-rose-700 border-rose-200", canceled: "bg-slate-100 text-slate-700 border-slate-200", owner: "bg-indigo-50 text-indigo-700 border-indigo-200" };

const contactLink = (p) => `https://wa.me/${p.whatsapp}?text=${encodeURIComponent("Olá! Tenho interesse no plano Clínica (personalizado) e gostaria de uma proposta para a minha clínica.")}`;

export function PlanCards({ plans, yearly, currentPlan, onChoose, loadingKey, coupon }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
      {plans.map((p) => {
        const basePrice = yearly ? p.yearly : p.monthly;
        const price = applyCoupon(basePrice, coupon);
        const isCurrent = currentPlan === p.id;
        return (
          <div key={p.id} className={`card-crisp p-6 flex flex-col relative ${p.highlight ? "ring-2 ring-indigo-600" : ""}`} data-testid={`plan-card-${p.id}`}>
            {p.highlight && <span className="absolute -top-3 left-6 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Mais escolhido</span>}
            <h3 className="font-heading text-xl font-bold text-slate-900">{p.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className={`font-extrabold text-slate-900 ${p.contact ? "font-heading text-2xl" : "font-mono-nums text-3xl"}`}>{p.contact ? "Sob consulta" : formatCurrency(price)}</span>
              {!p.contact && <span className="text-sm text-slate-500">/{yearly ? "ano" : "mês"}</span>}
            </div>
            {coupon && !p.contact && price !== basePrice && <div className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`plan-discount-${p.id}`}><span className="line-through text-slate-400 mr-1">{formatCurrency(basePrice)}</span>com cupom {coupon.code} · {couponLabel(coupon)}</div>}
            {yearly && !p.contact && <div className="text-xs text-emerald-700 mt-1">equivale a {formatCurrency(p.yearly / 12)}/mês · 2 meses grátis</div>}
            <ul className="mt-5 space-y-2 text-sm text-slate-700 flex-1">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-indigo-600 mt-0.5 shrink-0" weight="bold" />{f}</li>)}
            </ul>
            {p.contact ? (
              <Button asChild className="mt-6 w-full bg-slate-900 hover:bg-slate-800"><a href={contactLink(p)} target="_blank" rel="noreferrer" data-testid={`plan-contact-${p.id}`}>Falar com consultor</a></Button>
            ) : (
              <Button onClick={() => onChoose(p)} disabled={!!loadingKey} className={`mt-6 w-full ${p.highlight ? "bg-indigo-600 hover:bg-indigo-700" : ""}`} variant={p.highlight ? "default" : "outline"} data-testid={`plan-choose-${p.id}`}>
                {loadingKey === p.id ? "Redirecionando..." : isCurrent ? "Renovar / alterar ciclo" : "Assinar"}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/billing/me"), api.get("/billing/plans")])
      .then(([m, p]) => { setInfo(m.data); setPlans(p.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const choose = async (plan) => {
    setLoadingKey(plan.id);
    try {
      const r = await api.post("/billing/checkout", { lookup_key: `${plan.id}_${yearly ? "anual" : "mensal"}`, origin_url: window.location.origin, coupon_code: coupon?.code || null });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoadingKey(null);
    }
  };

  const _chooseCombo = async (plan) => {
    setLoadingKey(`combo-${plan.id}`);
    try {
      const r = await api.post("/billing/checkout", { lookup_key: `${plan.id}_combo_mensal`, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setLoadingKey(null); }
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!info) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div data-testid="billing-page">
      <PageHeader title="Meu plano" subtitle="Acompanhe seu uso, teste grátis e assinatura." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
        <div className="card-crisp p-6 lg:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={20} weight="duotone" className="text-indigo-600" />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Plano atual</span>
          </div>
          <div className="font-heading text-2xl font-bold text-slate-900" data-testid="current-plan-name">{info.plan_name}</div>
          <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_CLASS[info.status]}`} data-testid="current-plan-status">{STATUS_LABEL[info.status]}</span>
          {info.until && (
            <p className="text-sm text-slate-600 mt-4" data-testid="plan-until">
              {info.status === "trial" ? `Seu teste grátis termina em ${info.days_left} dia(s)` : info.status === "expired" ? "Acesso em modo leitura. Assine para voltar a editar." : info.cancel_at_period_end ? `Cancelado — acesso até ${new Date(info.until).toLocaleDateString("pt-BR")}` : `Renova em ${new Date(info.until).toLocaleDateString("pt-BR")}`}
            </p>
          )}
          {user?.stripe_customer_id && (
            <Button variant="outline" size="sm" className="mt-4" onClick={openPortal} data-testid="billing-portal">
              <ArrowSquareOut size={14} className="mr-1" /> Gerenciar pagamento / cancelar
            </Button>
          )}
        </div>
        <div className="card-crisp p-6 lg:col-span-2 space-y-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Uso do plano</div>
          <UsageBar label="Profissionais" used={info.usage.professionals} limit={info.limits.professionals} />
          <UsageBar label="Procedimentos" used={info.usage.procedures} limit={info.limits.procedures} />
          <UsageBar label="Orçamentos neste mês" used={info.usage.quotes_month} limit={info.limits.quotes_month} />
        </div>
      </div>

      <div className="mb-10"><ReferralCard /></div>

      <div className="mb-10 rounded-2xl border border-[#E3D3AE] bg-gradient-to-br from-[#FBF6EA] via-white to-[#F5ECD6] p-6 md:p-8" data-testid="partner-section">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#8F6E2E]"><Monitor size={16} weight="fill" /> Parceiro: Seu Chamador</div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mt-2">Assinante do Precificador ganha 30% no painel de chamada da TV</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">Use o cupom na assinatura em <a href="https://oseuchamador.com" target="_blank" rel="noreferrer" className="underline font-medium">oseuchamador.com</a>. Já usa o Chamador? Informe o código <code className="font-mono-nums font-bold">PRECIFICADOR</code> no pagamento aqui e ganhe 30% também.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {info.status === "active" || info.is_owner ? <code className="rounded-md bg-white border border-[#E3D3AE] px-4 py-2 font-mono-nums font-bold text-[#8F6E2E] text-lg" data-testid="partner-coupon">PARCEIRO</code> : <span className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="partner-coupon-locked">O cupom é liberado assim que você assinar um plano.</span>}
              <Button asChild className="bg-[#C5A059] hover:bg-[#B08E47] text-white rounded-full"><a href="https://oseuchamador.com/login?signup=1" target="_blank" rel="noreferrer" data-testid="partner-go">Assinar o Seu Chamador</a></Button>
            </div>
          </div>

      {!info.is_owner && (
        <>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="font-heading text-lg font-bold text-slate-900">Escolha seu plano</h2>
            <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm" data-testid="billing-cycle-toggle">
              <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full ${!yearly ? "bg-white shadow font-semibold" : "text-slate-600"}`} data-testid="cycle-monthly">Mensal</button>
              <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full ${yearly ? "bg-white shadow font-semibold" : "text-slate-600"}`} data-testid="cycle-yearly">Anual <span className="text-emerald-600 text-xs">2 meses grátis</span></button>
            </div>
          </div>
          <div className="mb-6 max-w-xl"><CouponBox coupon={coupon} onChange={setCoupon} /></div>
          <PlanCards plans={plans} yearly={yearly} currentPlan={info.status === "active" ? info.plan : null} onChoose={choose} loadingKey={loadingKey} coupon={coupon} />
          <p className="text-xs text-slate-500 mt-6">Pagamento seguro via Stripe. Cartão de crédito ou Pix. Cancele quando quiser.</p>

        </>
      )}
    </div>
  );
}
